import React, { FC, useEffect, useState } from "react";

import {
  KpkApiMediaDownloadLevel,
  KpkApiPublicShareExportDefinition,
  KpkApiPublicShareStatus,
} from "@keepeek/api-client";
import {
  ShareManagerSelectionType,
  ShareManagerUnitaryPublicShareEmbed,
  ShareSizingType,
  shareUnitaryPublicShareDeactivationDateState,
  shareUnitaryPublicShareDownloadLevelState,
  shareUnitaryPublicShareSizeHeightState,
  shareUnitaryPublicShareSizeState,
  shareUnitaryPublicShareSizeWidthState,
  updateUnitaryPublicSharedState,
  useCurrentUser,
  useShareManager,
} from "@keepeek/commons";
import {
  BaseComponentProps,
  KButton,
  KModal,
  KModalContentSection,
  KModalHeader,
  KModalSize,
  ShareEntryPoint,
} from "@keepeek/refront-components";
import { HelpOutlineOutlined } from "@mui/icons-material";
import { DatePicker } from "@mui/lab";
import AdapterDayjs from "@mui/lab/AdapterDayjs";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Input,
  InputBase,
  InputLabel,
  Link,
  MenuItem,
  Select,
  styled,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import clsx from "clsx";
import copy from "copy-to-clipboard";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { KIcon } from "../../components/common/KIcon";
import { CONFIGURATION_SECTION_PATH } from "../../components/layout/adminMenu";
import { ShareManager as ShareManagerConfig } from "../../models/configuration/components/shareManager";
import { useConfiguration } from "../../providers/config/hooks";
import { configSectionComponentDownloadManagerSelector } from "../../providers/config/selectors";
import UnitaryPublicShareActivate from "./UnitaryPublicShareActivate";

export type ShareManagerProps = {} & BaseComponentProps;

const PREFIX = "UnitaryPublicShareEntryPoint";

const classes = {
  entryPointContainer: `${PREFIX}-entryPointContainer`,
  entryPoint: `${PREFIX}-entryPoint`,
  entryPointArea: `${PREFIX}-entryPointArea`,
  entryPointButton: `${PREFIX}-entryPointButton`,
  exportDefinitionContainer: `${PREFIX}-exportDefinitionContainer`,
  attachmentsContainer: `${PREFIX}-attachmentsContainer`,
  deactivationDateContainer: `${PREFIX}-deactivationDateContainer`,
  sizeTypeSelection: `${PREFIX}-sizeTypeSelection`,
};

const StyledKModalContentSection = styled(KModalContentSection)(({ theme }) => ({
  [`& .${classes.entryPointContainer}`]: {
    display: "grid",
    gridTemplateAreas: `"input button"`,
    gridTemplateColumns: "auto max-content",
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateAreas: `"input" "button"`,
    },
  },

  [`& .${classes.entryPointArea}`]: {
    gridArea: "input",
    border: "1px solid",
    borderRadius: theme.shape.borderRadius,
    borderColor: theme.palette.grey[400],
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    color: theme.palette.common.black,
  },

  [`& .${classes.entryPointButton}`]: {
    gridArea: "button",
  },

  [`& .${classes.exportDefinitionContainer}`]: {
    display: "flex",
    alignItems: "center",
    paddingTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  [`& .${classes.attachmentsContainer}`]: {
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  [`& .${classes.deactivationDateContainer}`]: {
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  [`& .${classes.sizeTypeSelection}`]: {
    marginTop: theme.spacing(1),
    display: "flex",
    flexDirection: "row",
    alignItems: "baseline",
    gap: theme.spacing(0.5),
    paddingRight: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
}));

const labelId = "UnitaryPublicShareEntryPoint-input-label";

/**
 * The Share Manager only support for the moment:
 * - One selection of one id
 * - A selection of only a "unitary public share"
 *
 * TODO: evolve this to support shared basket, providers are ready
 * @returns Share manager modal component
 */
const ShareManager: FC<React.PropsWithChildren<ShareManagerProps>> = function ({
  className,
  id,
  testId = "ShareManager",
  sx,
}) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const {
    managerOpen,
    managerSelection: {
      selectionsResolved: selections,
      loading: loadingSelection,
      emptySelections,
    },
    managerActivation: { handleUpdateSharePublic },
    managerExportDefinition: {
      exportDefinitions,
      selectedExportDefinition,
      setSelectedExportDefinition,
    },
  } = useShareManager({
    onUpdateShareManagerError: () => {
      enqueueSnackbar(t("share-manager.update.public-share.error.label"), {
        variant: "error",
      });
    },
  });

  // Only take the first selection at the moment
  // Only take the unitary public share at the moment
  // TODO: evolve to multi select and multi types

  const [unitaryPublicShare, setUnitaryPublicShare] =
    useState<ShareManagerUnitaryPublicShareEmbed>();

  const [sizeType, setSizeType] = useRecoilState(shareUnitaryPublicShareSizeState);
  const [sizeHeight, setSizeHeight] = useRecoilState(shareUnitaryPublicShareSizeHeightState);
  const [sizeWidth, setSizeWidth] = useRecoilState(shareUnitaryPublicShareSizeWidthState);
  const [deactivationDate, setDeactivationDate] = useRecoilState(
    shareUnitaryPublicShareDeactivationDateState,
  );

  const [downloadLevel, setDownloadLevel] = useRecoilState(
    shareUnitaryPublicShareDownloadLevelState,
  );

  const setUpdateUnitaryPublicShared = useSetRecoilState(updateUnitaryPublicSharedState);

  const [invalidDate, setInvalidDate] = useState<boolean>(false);

  const shareManagerConfig = useConfiguration<ShareManagerConfig>(
    CONFIGURATION_SECTION_PATH.COMPONENTS_SHARE_MANAGER,
  );
  const unitaryPublicShareConfig = shareManagerConfig?.unitaryPublicShare;
  const [selectedAttachmentsVisible, setSelectedAttachmentsVisible] = useState(false);

  const configDownloadManager = useRecoilValue(configSectionComponentDownloadManagerSelector);

  const { hasAttachmentsRights } = useCurrentUser();

  useEffect(() => {
    if (!loadingSelection) {
      const selection = selections.find(
        (s) => s.type === ShareManagerSelectionType.UNITARY_PUBLIC_SHARE,
      );
      setUnitaryPublicShare(selection?.resolved?.[0]);
      setDeactivationDate(selection?.resolved?.[0]?.deactivationDate);
      setDownloadLevel(selection?.resolved?.[0]?.downloadLevel as KpkApiMediaDownloadLevel);
      if (selection?.resolved?.[0]?.attachmentVisible) {
        setSelectedAttachmentsVisible(selection?.resolved?.[0]?.attachmentVisible);
      }

      if (selection?.resolved?.[0]?.exportDefinition) {
        setSelectedExportDefinition(selection?.resolved?.[0]?.exportDefinition);
      } else {
        let exportDefinition;
        if (selection?.resolved?.[0]?.downloadLevel === KpkApiMediaDownloadLevel.HD) {
          exportDefinition = KpkApiPublicShareExportDefinition.HD;
        } else if (selection?.resolved?.[0]?.downloadLevel === KpkApiMediaDownloadLevel.Bd) {
          exportDefinition = KpkApiPublicShareExportDefinition.Bd;
        } else {
          exportDefinition = KpkApiPublicShareExportDefinition.None;
        }

        setSelectedExportDefinition(exportDefinition);
      }
    }
  }, [
    loadingSelection,
    selections,
    setDeactivationDate,
    setDownloadLevel,
    setSelectedExportDefinition,
    setSelectedAttachmentsVisible,
  ]);

  if (!managerOpen.isOpen) {
    return null;
  }

  const snackbarCallback = ({ message, options }) => enqueueSnackbar(message, options);
  const copyEntryPoint = (link: string) => {
    try {
      copy(link);
      snackbarCallback({
        message: t("share-manager.unitary-public-share.copy-link.notification-success"),
        options: {
          variant: "info",
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <KModal
      id={id}
      data-testid={testId}
      className={clsx("ShareManager", className)}
      sx={sx}
      open={managerOpen.isOpen}
      onClose={emptySelections}
      header={
        <KModalHeader onClose={emptySelections} title={t("share-manager.modal.title") as string} />
      }
      size={KModalSize.Large}
      loading={loadingSelection || !unitaryPublicShare}
    >
      {unitaryPublicShare && (
        <Box sx={{ position: "relative", minHeight: "250px" }}>
          {unitaryPublicShare.status === KpkApiPublicShareStatus.Deactivated && (
            <UnitaryPublicShareActivate
              mediaId={unitaryPublicShare.elementId}
              downloadLevel={downloadLevel}
            />
          )}
          {!unitaryPublicShareConfig?.disableDeactivate && (
            <StyledKModalContentSection sx={{ paddingBottom: (theme) => theme.spacing(1) }}>
              <Box className={classes.deactivationDateContainer}>
                <Typography
                  component={"span"}
                  variant={"body1"}
                  sx={{ marginRight: (theme) => theme.spacing(0.5) }}
                >
                  {t("share-manager.unitary-public-share.action.deactivate-date-share.label")}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    inputFormat={t("input.date.format") as string}
                    value={deactivationDate || null}
                    onChange={async (e) => {
                      if (e) {
                        const date = new Date(e);
                        try {
                          Date.parse(date.toISOString());
                          setInvalidDate(false);
                          await handleUpdateSharePublic(
                            unitaryPublicShare.elementId,
                            unitaryPublicShare.status as KpkApiPublicShareStatus,
                            selectedExportDefinition,
                            date,
                            selectedAttachmentsVisible,
                          );
                          setDeactivationDate(date);
                        } catch (e) {
                          setInvalidDate(true);
                        }
                      } else {
                        setInvalidDate(false);
                        await handleUpdateSharePublic(
                          unitaryPublicShare.elementId,
                          unitaryPublicShare.status as KpkApiPublicShareStatus,
                          selectedExportDefinition,
                          undefined,
                          selectedAttachmentsVisible,
                        );
                      }
                    }}
                    renderInput={(inputProps: JSX.IntrinsicAttributes) => (
                      <TextField
                        margin="none"
                        id="UnitaryPublicShareDeactivationDateInput"
                        {...inputProps}
                        autoComplete={"off"}
                        variant="standard"
                        size={"small"}
                        helperText={
                          invalidDate
                            ? t("share-manager.unitary-public-share.invalidDate.label")
                            : undefined
                        }
                        color={invalidDate ? "error" : undefined}
                        sx={{ "& input": { padding: "unset" } }}
                      />
                    )}
                  />
                </LocalizationProvider>
                <Tooltip
                  title={
                    t(
                      "share-manager.unitary-public-share.action.deactivate-tooltip.label",
                    ) as string
                  }
                  sx={{ marginLeft: "20px" }}
                >
                  <HelpOutlineOutlined></HelpOutlineOutlined>
                </Tooltip>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                }}
              >
                <Typography
                  component={"span"}
                  variant={"body1"}
                  sx={{ marginRight: (theme) => theme.spacing(0.5) }}
                >
                  {t("share-manager.unitary-public-share.action.deactivate-share-or.label")}
                </Typography>
                <Link
                  component={"button"}
                  variant={"body1"}
                  onClick={async () => {
                    await handleUpdateSharePublic(
                      unitaryPublicShare.elementId,
                      KpkApiPublicShareStatus.Deactivated,
                      selectedExportDefinition,
                      undefined,
                      selectedAttachmentsVisible,
                    );
                    setUpdateUnitaryPublicShared((prev) => prev + 1);
                  }}
                >
                  {t("share-manager.unitary-public-share.action.deactivate-share.label")}
                </Link>
              </Box>
            </StyledKModalContentSection>
          )}
          {!unitaryPublicShareConfig?.disableDetailsLink && (
            <StyledKModalContentSection
              title={<span id={labelId}>{t("share-manager.unitary-public-share.link.label")}</span>}
              sx={{ paddingBottom: (theme) => theme.spacing(2) }}
            >
              <ShareEntryPoint
                entryPoint={unitaryPublicShare.detailLink}
                entryPointInputId={"ShareEntryPoint-UnitaryPublicShare"}
                copyEntryPoint={() => {
                  if (unitaryPublicShare.detailLink) {
                    copyEntryPoint(unitaryPublicShare.detailLink);
                  }
                }}
                loading={loadingSelection}
                shareTitle={unitaryPublicShare.title}
                disableSocialNetworksLink={unitaryPublicShareConfig?.disableSocialNetworksLink}
                KIcon={KIcon}
              />

              {!unitaryPublicShareConfig?.disableExportDefinition && (
                <Box className={classes.exportDefinitionContainer}>
                  <Typography
                    component={"span"}
                    variant={"body1"}
                    sx={{ marginRight: (theme) => theme.spacing(1) }}
                  >
                    {t("share-manager.unitary-public-share.exportDefinition.label")}
                  </Typography>
                  <Select
                    size={"small"}
                    value={selectedExportDefinition}
                    onChange={(e) => {
                      handleUpdateSharePublic(
                        unitaryPublicShare.elementId,
                        unitaryPublicShare.status as KpkApiPublicShareStatus,
                        e.target.value as KpkApiPublicShareExportDefinition,
                        deactivationDate,
                        selectedAttachmentsVisible,
                      );
                      setSelectedExportDefinition(
                        e.target.value as KpkApiPublicShareExportDefinition,
                      );
                      // Uncheck attachments visible while moving from HD definition
                      if (
                        selectedAttachmentsVisible &&
                        e.target.value !== KpkApiPublicShareExportDefinition.HD
                      ) {
                        setSelectedAttachmentsVisible(false);
                      }
                    }}
                  >
                    {(downloadLevel === KpkApiMediaDownloadLevel.None ||
                      downloadLevel === KpkApiMediaDownloadLevel.Bd ||
                      downloadLevel === KpkApiMediaDownloadLevel.HD) && (
                      <MenuItem key={exportDefinitions[2].id} value={exportDefinitions[2].id}>
                        {exportDefinitions[2].title}
                      </MenuItem>
                    )}
                    {(downloadLevel === KpkApiMediaDownloadLevel.Bd ||
                      downloadLevel === KpkApiMediaDownloadLevel.HD) && (
                      <MenuItem key={exportDefinitions[0].id} value={exportDefinitions[0].id}>
                        {exportDefinitions[0].title}
                      </MenuItem>
                    )}
                    {downloadLevel === KpkApiMediaDownloadLevel.HD && (
                      <MenuItem key={exportDefinitions[1].id} value={exportDefinitions[1].id}>
                        {exportDefinitions[1].title}
                      </MenuItem>
                    )}
                  </Select>
                </Box>
              )}

              {!unitaryPublicShareConfig?.disableExportDefinition &&
                configDownloadManager?.enableOtherFormats &&
                hasAttachmentsRights && (
                  <Box className={classes.attachmentsContainer}>
                    <FormControlLabel
                      disabled={selectedExportDefinition !== KpkApiPublicShareExportDefinition.HD}
                      control={
                        <Checkbox
                          id="PublicUnitaryShareAttachmentsVisibleInput"
                          aria-label={
                            t(
                              "share-manager.unitary-public-share.attachments-visible.input.label",
                            ) as string
                          }
                          checked={selectedAttachmentsVisible}
                          onChange={(e) => {
                            handleUpdateSharePublic(
                              unitaryPublicShare.elementId,
                              unitaryPublicShare.status as KpkApiPublicShareStatus,
                              selectedExportDefinition as KpkApiPublicShareExportDefinition,
                              deactivationDate,
                              e.target.checked,
                            );
                            setSelectedAttachmentsVisible(e.target.checked);
                          }}
                        />
                      }
                      label={
                        t(
                          "share-manager.unitary-public-share.attachments-visible.checkbox.label",
                        ) as string
                      }
                    />
                  </Box>
                )}
            </StyledKModalContentSection>
          )}
          {!unitaryPublicShareConfig?.disableFileLink && (
            <StyledKModalContentSection
              title={
                <span id={labelId}>{t("share-manager.unitary-public-share.file-link.label")}</span>
              }
              sx={{ paddingBottom: (theme) => theme.spacing(2) }}
            >
              <ShareEntryPoint
                entryPoint={unitaryPublicShare.link}
                entryPointInputId={"ShareEntryPoint-UnitaryPublicShare-fileLink"}
                copyEntryPoint={() => {
                  if (unitaryPublicShare.link) {
                    copyEntryPoint(unitaryPublicShare.link);
                  }
                }}
                loading={loadingSelection}
                shareTitle={unitaryPublicShare.title}
                disableSocialNetworksLink={unitaryPublicShareConfig?.disableSocialNetworksLink}
                KIcon={KIcon}
              />
            </StyledKModalContentSection>
          )}
          {!unitaryPublicShareConfig?.disableEmbed && (
            <StyledKModalContentSection
              title={
                <span id={`${labelId}-embedded`}>
                  {t("share-manager.unitary-public-share.embed.label")}
                </span>
              }
              sx={{ paddingBottom: (theme) => theme.spacing(2) }}
            >
              <Box
                className={classes.entryPointContainer}
                sx={{ paddingTop: (theme) => theme.spacing(1) }}
              >
                <InputBase
                  className={classes.entryPointArea}
                  value={unitaryPublicShare.embed}
                  readOnly
                  fullWidth
                  multiline
                  inputProps={{ "aria-labelledby": `${labelId}-embedded` }}
                />
                <KButton
                  onClick={() => {
                    if (unitaryPublicShare.embed) {
                      copyEntryPoint(unitaryPublicShare.embed);
                    }
                  }}
                  title={t("share-manager.unitary-public-share.action.copy.label") as string}
                  variant="outlined"
                  startIcon={<KIcon className="fa-copy" />}
                  className={classes.entryPointButton}
                  disabled={loadingSelection}
                >
                  {t("share-manager.unitary-public-share.action.copy.label")}
                </KButton>
              </Box>
              <Box className={classes.sizeTypeSelection}>
                <InputLabel>{t("share-manager.unitary-public-share.sizeType.label")}</InputLabel>
                <ToggleButtonGroup
                  color="primary"
                  value={sizeType}
                  exclusive
                  onChange={(event, value) => {
                    if (value) {
                      setSizeType(value);
                    }
                  }}
                  aria-label="Platform"
                  size="small"
                >
                  <ToggleButton value={ShareSizingType.AUTO}>
                    {t("share-manager.unitary-public-share.sizeType.automatic.label")}
                  </ToggleButton>
                  <ToggleButton value={ShareSizingType.MANUAL}>
                    {t("share-manager.unitary-public-share.sizeType.manual.label")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {sizeType === ShareSizingType.MANUAL && (
                <FormControl
                  sx={{
                    marginLeft: (theme) => theme.spacing(3),
                    marginTop: (theme) => theme.spacing(2),
                  }}
                >
                  <FormGroup sx={{ marginBottom: 1 }}>
                    <FormControlLabel
                      label={t("share-manager.unitary-public-share.size.height.label") as string}
                      labelPlacement="start"
                      control={
                        <Input
                          sx={{ marginLeft: 2 }}
                          size="small"
                          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                          type="number"
                          value={sizeHeight}
                          onChange={(event) => {
                            if (event.target.value) {
                              setSizeHeight(parseInt(event.target.value));
                            } else {
                              setSizeHeight(0);
                            }
                          }}
                        />
                      }
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormControlLabel
                      label={t("share-manager.unitary-public-share.size.width.label") as string}
                      labelPlacement="start"
                      control={
                        <Input
                          sx={{ marginLeft: 2 }}
                          size="small"
                          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                          type="number"
                          value={sizeWidth}
                          onChange={(event) => {
                            if (event.target.value) {
                              setSizeWidth(parseInt(event.target.value));
                            } else {
                              setSizeWidth(0);
                            }
                          }}
                        />
                      }
                    />
                  </FormGroup>
                </FormControl>
              )}
            </StyledKModalContentSection>
          )}
        </Box>
      )}
    </KModal>
  );
};

export default ShareManager;
